import { Icon, Typography } from '@packages/ui/shared';
import { docsService } from '@web/services/singletons';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PublicationModal } from './publication-modal';
import { publicationsActions } from './context/publications.state';
import { usePublicationsDispatch } from './context/publications.hooks';
import { PublicationType } from './publication-type-icons';
import { createUseStyles } from 'react-jss';
import colors from '@packages/core/styles/colors';
import { getLogger } from '@packages/core/logging';
import { PublicationForm } from '@web/vin-sessions/types';

const logger = getLogger('vin-sessions');

const useStyles = createUseStyles({
    cardContainer: {
        display: 'flex',
        alignItems: 'center',
        gap: 16,
        border: `1px solid ${colors.borderDark}`,
        padding: 16,
        borderRadius: 4,
        marginBottom: 8,
        width: '100%',
    },
    iconWrapper: {
        width: '4%',
    },
    contentWrapper: {
        display: 'flex',
        flexDirection: 'column',
        width: '90%',
    },
    title: {
        color: '#007BFF',
        cursor: 'pointer',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    docCode: {
        fontFamily: 'Roboto',
        fontWeight: 400,
        fontStyle: 'normal',
        fontSize: 13,
        lineHeight: '16px',
        letterSpacing: 0,
        color: colors.textDarkSecondary,
    },
    actionWrapper: {
        width: '10%',
        display: 'flex',
        justifyContent: 'flex-end',
    },
});

interface Props {
    associatedDocs: {
        associatedDocId: string;
        mappedDocId: string;
        title: string;
        docCode: string;
        docTypeId: string;
    }[];
    setAssociatedPublications: React.Dispatch<React.SetStateAction<any>>;
    filterResponse: any;
    publicationInfo: PublicationForm;
    setPublicationInfo: any;
}

export const AssociatePublicationsCard = ({
    associatedDocs,
    setAssociatedPublications,
    filterResponse,
    publicationInfo,
    setPublicationInfo,
}: Props) => {
    const { t } = useTranslation();
    const [showDeletePublication, setShowDeletePublication] = useState<boolean>(false);
    const [associatedDocId, setAssociatedDocId] = useState<string>('');
    const publicationDispatch = usePublicationsDispatch();
    const classes = useStyles();

    const deleteAssociatedPublication = useCallback(async () => {
        try {
            const response = await docsService.deleteAssociatedPublication({
                associatedDocId: associatedDocId,
            });
            if (response.success && response.data) {
                setAssociatedPublications((prev: Props['associatedDocs']) =>
                    prev.filter((doc) => doc.associatedDocId !== associatedDocId)
                );
                setAssociatedDocId('');
                setShowDeletePublication(false);
                publicationDispatch(
                    publicationsActions.setToastMessage({
                        toast: {
                            toastMessage: 'Publication successfully removed',
                            iconColor: 'white',
                            iconName: 'checkmark',
                        },
                    })
                );
            }
        } catch (error) {
            logger.error(`Failed to delete the associated publication [${associatedDocId}]`, {
                error,
                associatedDocId,
            });
            publicationDispatch(
                publicationsActions.setToastMessage({
                    toast: {
                        toastMessage: 'Error in removing Associated Publication',
                        iconColor: 'white',
                        iconName: 'x-close',
                    },
                })
            );
        }
    }, [associatedDocId, setAssociatedPublications, publicationDispatch]);

    useEffect(() => {
        const runEffect = async () => {
            if (
                publicationInfo?.associatedDocuments !== undefined &&
                publicationInfo?.associatedDocuments !== null &&
                JSON.stringify(publicationInfo.associatedDocuments.map((doc: any) => doc.documentId).sort()) ===
                    JSON.stringify(associatedDocs.map((doc) => doc.mappedDocId).sort())
            )
                setPublicationInfo({ associatePublicationChange: false });
            else {
                setPublicationInfo({ associatePublicationChange: true });
            }
        };
        runEffect();
    }, [associatedDocs, associatedDocId]);

    return (
        <>
            <PublicationModal
                show={showDeletePublication}
                title={t('stisPublications:modal.deleteTitle', 'Save changes as draft and exit')}
                bodyHeader={t('Are you sure you want to remove this associated vehicle?')}
                bodyContent={t('It will be removed permanently and cannot be undone.')}
                cancelButton={t('stisPublications:buttons.cancel', 'Cancel')}
                confirmButton={t('Remove')}
                confirmButtonLoading={false}
                handleCancelAction={() => setShowDeletePublication(false)}
                handleConfirmAction={deleteAssociatedPublication}
                onHideModal={() => setShowDeletePublication(false)}
                variant="danger"
            />
            {associatedDocs.length > 0 &&
                associatedDocs.map((doc) => (
                    <div className={classes.cardContainer} key={doc.associatedDocId}>
                        <div className={classes.iconWrapper}>
                            <PublicationType
                                publicationType={
                                    filterResponse?.publicationType.find(
                                        (publication: { id: string; description: string }) =>
                                            publication.id === doc.docTypeId
                                    )?.description
                                }
                                size={40}
                            />
                        </div>
                        <div className={classes.contentWrapper}>
                            <div className={classes.title} title={doc.title}>
                                {doc.title}
                            </div>
                            <div className={classes.docCode}>
                                <Typography>{doc.docCode}</Typography>
                            </div>
                        </div>
                        <div className={classes.actionWrapper}>
                            <Icon
                                size={30}
                                name="x-close"
                                color="blueOne"
                                raisedContainerStyle={{ width: 22, height: 22 }}
                                onPress={() => {
                                    setShowDeletePublication(true);
                                    setAssociatedDocId(doc.associatedDocId);
                                }}
                            />
                        </div>
                    </div>
                ))}
        </>
    );
};
